import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cc_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("cc_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // For flows that already hold a fresh JWT (claim accept, password reset):
  // store the session directly — no /auth/login round-trip.
  const adoptSession = (token, u) => {
    localStorage.setItem("cc_token", token);
    setUser(u);
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("cc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("cc_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, adoptSession, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

/** Platform admins (founder/staff) bypass all paywalls. Use this in any page
 *  that shows pricing or pay buttons — wrap the gated UI in {!isAdmin && ...}
 *  and render <AdminUnlockedBanner /> instead. */
export const useIsAdmin = () => {
  const { user } = useAuth();
  return !!(user && user.is_platform_admin);
};
